import { c as _c } from "react/compiler-runtime";
import React from "react";
import { getHighlightedSpans } from "@/lib/logHighlighting";
import { cn } from "@/lib/utils";
interface LogLineTextProps {
  text: string;
  searchTerms?: string[];
  caseSensitive?: boolean;
}
type AnsiSgrState = {
  fg?: AnsiColor;
  bg?: AnsiColor;
  bold?: boolean;
  dim?: boolean;
  underline?: boolean;
  inverse?: boolean;
};
type AnsiPaletteKind = "fg" | "bg";
type AnsiColor = {
  kind: "base16";
  idx: number;
} | {
  kind: "css";
  value: string;
};
function ansiBase16ToCssVar(kind: AnsiPaletteKind, idx: number): string {
  // Defined in `src/styles/globals.css` as:
  // - `--ansi-fg-0` ... `--ansi-fg-15`
  // - `--ansi-bg-0` ... `--ansi-bg-15`
  // with `.dark` overrides.
  //
  // Foreground/background are intentionally separate so light mode can use
  // pastel background colors while keeping readable foreground contrast.
  const safe = Math.max(0, Math.min(15, Math.trunc(idx)));
  return `var(--ansi-${kind}-${safe})`;
}
function clampByte(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(255, n));
}
function ansi256ToColor(n: number): AnsiColor | undefined {
  if (!Number.isFinite(n)) return undefined;
  const idx = Math.max(0, Math.min(255, Math.trunc(n)));

  // 0-15: standard + bright colors
  if (idx <= 15) return {
    kind: "base16",
    idx
  };

  // 16-231: 6x6x6 color cube
  if (idx >= 16 && idx <= 231) {
    const c = idx - 16;
    const r = Math.floor(c / 36);
    const g = Math.floor(c % 36 / 6);
    const b = c % 6;
    const steps = [0, 95, 135, 175, 215, 255];
    return {
      kind: "css",
      value: `rgb(${steps[r]}, ${steps[g]}, ${steps[b]})`
    };
  }

  // 232-255: grayscale ramp
  const gray = 8 + (idx - 232) * 10;
  const g = clampByte(gray);
  return {
    kind: "css",
    value: `rgb(${g}, ${g}, ${g})`
  };
}
function applySgr(state: AnsiSgrState, codes: number[]): AnsiSgrState {
  let next: AnsiSgrState = {
    ...state
  };
  for (let i = 0; i < codes.length; i++) {
    const code = codes[i];
    if (!Number.isFinite(code)) continue;

    // Reset all
    if (code === 0) {
      next = {};
      continue;
    }

    // Intensity
    if (code === 1) {
      next.bold = true;
      continue;
    }
    if (code === 2) {
      next.dim = true;
      continue;
    }
    if (code === 22) {
      next.bold = false;
      next.dim = false;
      continue;
    }

    // Underline
    if (code === 4) {
      next.underline = true;
      continue;
    }
    if (code === 24) {
      next.underline = false;
      continue;
    }

    // Inverse
    if (code === 7) {
      next.inverse = true;
      continue;
    }
    if (code === 27) {
      next.inverse = false;
      continue;
    }

    // Foreground reset
    if (code === 39) {
      next.fg = undefined;
      continue;
    }

    // Background reset
    if (code === 49) {
      next.bg = undefined;
      continue;
    }

    // Foreground 16-color
    if (code >= 30 && code <= 37) {
      next.fg = {
        kind: "base16",
        idx: code - 30
      };
      continue;
    }
    if (code >= 90 && code <= 97) {
      next.fg = {
        kind: "base16",
        idx: code - 90 + 8
      };
      continue;
    }

    // Background 16-color
    if (code >= 40 && code <= 47) {
      next.bg = {
        kind: "base16",
        idx: code - 40
      };
      continue;
    }
    if (code >= 100 && code <= 107) {
      next.bg = {
        kind: "base16",
        idx: code - 100 + 8
      };
      continue;
    }

    // Extended colors (consume parameters so we don't accidentally interpret them
    // as separate SGR codes like "2" (dim)).
    if (code === 38 || code === 48) {
      const isForeground = code === 38;
      const mode = codes[i + 1];

      // 38;2;r;g;b or 48;2;r;g;b
      if (mode === 2 && i + 4 < codes.length) {
        const r = clampByte(codes[i + 2]);
        const g = clampByte(codes[i + 3]);
        const b = clampByte(codes[i + 4]);
        const color: AnsiColor = {
          kind: "css",
          value: `rgb(${r}, ${g}, ${b})`
        };
        if (isForeground) next.fg = color;else next.bg = color;
        i += 4;
        continue;
      }

      // 38;5;n or 48;5;n
      if (mode === 5 && i + 2 < codes.length) {
        const n = codes[i + 2];
        const color = ansi256ToColor(n);
        if (color) {
          if (isForeground) next.fg = color;else next.bg = color;
        }
        i += 2;
        continue;
      }

      // Unknown/unsupported extended mode; consume the mode param if present.
      if (Number.isFinite(mode)) {
        i += 1;
      }
      continue;
    }
  }
  return next;
}
function colorToCss(color: AnsiColor | undefined, kind: AnsiPaletteKind): string | undefined {
  if (!color) return undefined;
  if (color.kind === "base16") return ansiBase16ToCssVar(kind, color.idx);
  return color.value;
}
function effectiveSpanStyle(state: AnsiSgrState): React.CSSProperties {
  const effectiveFg = state.inverse ? state.bg : state.fg;
  const effectiveBg = state.inverse ? state.fg : state.bg;
  return {
    // Important: when SGR inverse (7) is active, we swap the *roles* of fg/bg.
    // If a color is a base16 index, we always resolve it via the palette
    // appropriate to the role (fg uses `--ansi-fg-*`, bg uses `--ansi-bg-*`),
    // preventing light-mode pastel backgrounds from becoming unreadable text.
    color: colorToCss(effectiveFg, "fg"),
    backgroundColor: colorToCss(effectiveBg, "bg"),
    fontWeight: state.bold ? "bold" : undefined,
    opacity: state.dim ? 0.72 : undefined,
    textDecoration: state.underline ? "underline" : undefined,
    whiteSpace: "inherit"
  };
}

// LogLineText handles ANSI colors and log line prefixes to style the text.
export const LogLineText = t0 => {
  const $ = _c(19);
  const {
    text,
    searchTerms: t1,
    caseSensitive: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const searchTerms = t3;
  const caseSensitive = t2 === undefined ? false : t2;
  let processedText = text;
  let lineStyle;
  let prefixElement;
  if ($[2] !== text) {
    lineStyle = {};
    prefixElement = null;
    if (text.startsWith("##[error]")) {
      processedText = text.slice(9);
      lineStyle.backgroundColor = "color-mix(in srgb, var(--chart-5) 8%, transparent)";
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <span style={{
          color: "var(--chart-5)"
        }}>Error: </span>;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      prefixElement = t4;
    } else {
      if (text.startsWith("##[warning]")) {
        processedText = text.slice(11);
        lineStyle.backgroundColor = "color-mix(in srgb, var(--chart-3) 8%, transparent)";
        let t4;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <span style={{
            color: "var(--chart-3)"
          }}>Warning: </span>;
          $[7] = t4;
        } else {
          t4 = $[7];
        }
        prefixElement = t4;
      } else {
        if (text.startsWith("##[debug]")) {
          processedText = text.slice(9);
          lineStyle.backgroundColor = "color-mix(in srgb, var(--chart-grey) 8%, transparent)";
          let t4;
          if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
            t4 = <span style={{
              color: "var(--chart-grey)"
            }}>Debug: </span>;
            $[8] = t4;
          } else {
            t4 = $[8];
          }
          prefixElement = t4;
        } else {
          if (text.startsWith("##[command]")) {
            processedText = text.slice(11);
            lineStyle.color = "var(--chart-1)";
          }
        }
      }
    }
    $[2] = text;
    $[3] = lineStyle;
    $[4] = prefixElement;
    $[5] = processedText;
  } else {
    lineStyle = $[3];
    prefixElement = $[4];
    processedText = $[5];
  }
  let t4;
  if ($[9] !== caseSensitive || $[10] !== searchTerms) {
    t4 = text_0 => {
      const parts = text_0.split(/(\x1b\[[0-9;]*m)/);
      let currentStyle = {};
      const elements = [];
      let textBuffer = "";
      let segmentIndex = 0;
      const renderTextWithHighlights = (text_1, keyPrefix) => {
        const urlRegex = /(https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+)/g;
        const parts_0 = text_1.split(urlRegex);
        return parts_0.map((part, idx) => {
          const urlTestRegex = /^https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+$/;
          if (urlTestRegex.test(part)) {
            let cleanUrl = part;
            let trailingPunctuation = "";
            const trailingPunctuationRegex = /([.,;:!?)"'>]+)$/;
            const match = cleanUrl.match(trailingPunctuationRegex);
            if (match) {
              trailingPunctuation = match[1];
              cleanUrl = cleanUrl.slice(0, -trailingPunctuation.length);
            }
            let urlContent = cleanUrl;
            let trailingContent = trailingPunctuation;
            if (searchTerms.length > 0) {
              const urlSpans = getHighlightedSpans(cleanUrl, searchTerms, caseSensitive);
              urlContent = urlSpans.map((span, spanIdx) => <span key={`${keyPrefix}-url-span-${idx}-${spanIdx}`} className={cn(span.isHighlighted && "bg-yellow-300 dark:bg-yellow-600/50 font-semibold")}>{span.text}</span>);
              if (trailingPunctuation) {
                const trailingSpans = getHighlightedSpans(trailingPunctuation, searchTerms, caseSensitive);
                trailingContent = trailingSpans.map((span_0, spanIdx_0) => <span key={`${keyPrefix}-trailing-span-${idx}-${spanIdx_0}`} className={cn(span_0.isHighlighted && "bg-yellow-300 dark:bg-yellow-600/50 font-semibold")}>{span_0.text}</span>);
              }
            }
            return <React.Fragment key={`${keyPrefix}-url-fragment-${idx}`}><a href={cleanUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer" onClick={_temp}>{urlContent}</a>{trailingContent}</React.Fragment>;
          } else {
            if (searchTerms.length === 0) {
              return part;
            }
            const spans = getHighlightedSpans(part, searchTerms, caseSensitive);
            return spans.map((span_1, spanIdx_1) => <span key={`${keyPrefix}-text-span-${idx}-${spanIdx_1}`} className={cn(span_1.isHighlighted && "bg-yellow-300 dark:bg-yellow-600/50 font-semibold")}>{span_1.text}</span>);
          }
        });
      };
      for (let index = 0; index < parts.length; index++) {
        const part_0 = parts[index];
        if (part_0.startsWith("\x1B[")) {
          if (textBuffer) {
            const style = effectiveSpanStyle(currentStyle);
            const key = `text-${segmentIndex++}`;
            elements.push(<span key={key} style={style}>{renderTextWithHighlights(textBuffer, key)}</span>);
            textBuffer = "";
          }
          const rawCodes = part_0.slice(2, -1);
          const codes = rawCodes ? rawCodes.split(";").map(_temp2) : [0];
          currentStyle = applySgr(currentStyle, codes);
        } else {
          if (part_0 !== undefined) {
            textBuffer = textBuffer + part_0;
          }
        }
      }
      if (textBuffer) {
        const style_0 = effectiveSpanStyle(currentStyle);
        elements.push(<span key={`final-${segmentIndex++}`} style={style_0}>{renderTextWithHighlights(textBuffer, `final-${segmentIndex}`)}</span>);
      }
      return elements;
    };
    $[9] = caseSensitive;
    $[10] = searchTerms;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const parseAnsiText = t4;
  const t5 = prefixElement;
  let t6;
  if ($[12] !== parseAnsiText || $[13] !== processedText) {
    t6 = parseAnsiText(processedText);
    $[12] = parseAnsiText;
    $[13] = processedText;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== lineStyle || $[16] !== prefixElement || $[17] !== t6) {
    t7 = <span className="font-mono" style={lineStyle}>{t5}{t6}</span>;
    $[15] = lineStyle;
    $[16] = prefixElement;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
function _temp(e) {
  e.stopPropagation();
}
function _temp2(v) {
  return Number(v);
}